/**
 * What a recovering cycle has spent, and what it is waiting for.
 *
 * Two budgets, never one. The normal budget buys the resting grid; the recovery
 * reserve is held back for the zones and belongs to this slot alone — it is not
 * capital another slot could use, and reporting one figure for both would make
 * a cycle look like it had money to work with that is already committed to its
 * own worst case.
 *
 * The zones are not orders. A price at −23 % is somewhere the strategy starts
 * watching, and nothing is committed there until the runtime decides. This
 * block names what would happen next and what the price has to do first, so
 * that "armed" is never read as "bought".
 *
 * Every figure is the server's. Absent ones stay absent: a dash is a smaller
 * error than a plausible number.
 */
import { money, percent, price } from "@/shared/money/money";
import { CYCLE_STATE_LABEL, type RecoveryReading } from "@/shared/strategy/recovery";

const ABSENT = "—";

function Figure({
  label,
  value,
  note,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-label-caps text-text-muted">{label}</span>
      <span
        data-testid={testId}
        className="font-numeric tabular-nums whitespace-nowrap text-text-primary"
      >
        {value}
      </span>
      {note !== undefined && (
        <span className="text-helper-sm text-text-muted">{note}</span>
      )}
    </span>
  );
}

export function Recovery({
  reading,
  slotId,
}: {
  readonly reading: RecoveryReading;
  readonly slotId: string;
}): React.JSX.Element {
  const amount = (raw: string | null): string => (raw === null ? ABSENT : money(raw));
  const rate = (raw: string | null): string => (raw === null ? ABSENT : percent(raw));

  return (
    <section
      data-testid={`recovery-${slotId}`}
      className="mb-3 rounded-row border border-border-subtle p-3"
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-label-caps tracking-[0.08em] text-text-muted">
          Восстановление
        </span>
        <span
          data-testid={`recovery-state-${slotId}`}
          className="text-helper-sm text-text-secondary"
        >
          {CYCLE_STATE_LABEL[reading.state]}
        </span>
      </div>

      <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          label="Средняя цена"
          value={reading.vwap === null ? ABSENT : price(reading.vwap)}
          testId={`recovery-vwap-${slotId}`}
        />
        <Figure
          label="Вложено маржи"
          value={amount(reading.invested_margin)}
          testId={`recovery-invested-${slotId}`}
        />
        <Figure
          label="Бюджет сетки"
          value={amount(reading.normal_budget_remaining)}
          note="осталось"
          testId={`recovery-budget-${slotId}`}
        />
        {/* Held for this slot's own worst case. It is not free capital, and a
            screen that counted it as free would offer the same money twice. */}
        <Figure
          label="Резерв восстановления"
          value={amount(reading.recovery_reserve_remaining)}
          note="осталось · занято этим слотом"
          testId={`recovery-reserve-${slotId}`}
        />
        <Figure
          label="Следующая зона"
          value={reading.next_zone ?? ABSENT}
          note="зона активации, не ордер"
          testId={`recovery-next-zone-${slotId}`}
        />
        <Figure
          label="Локальный минимум"
          value={reading.local_low === null ? ABSENT : price(reading.local_low)}
          testId={`recovery-local-low-${slotId}`}
        />
        <Figure
          label="Нужен откат"
          value={rate(reading.retracement_needed)}
          note="от минимума, чтобы докупить"
          testId={`recovery-retracement-${slotId}`}
        />
        <Figure
          label="Версия стратегии"
          value={reading.strategy_version ?? ABSENT}
          testId={`recovery-version-${slotId}`}
        />
      </div>
    </section>
  );
}
